'use client';

import classes from './ProcessTimeline.module.scss';
import { IconCheck, IconRocket, IconTarget } from '@tabler/icons-react';

type TimelineStep = {
  title: string;
  description: string;
  duration?: string;
  outcome?: string;
};

type ProcessTimelineProps = {
  badge?: string;
  title: string;
  subtitle?: string;
  steps: TimelineStep[];
  footerNote?: string;
  footerNoteHighlight?: string;
};

const stepIcons = [IconTarget, IconRocket, IconCheck];

export const ProcessTimeline = ({
  data,
  className,
}: {
  data: ProcessTimelineProps;
  className?: string;
}) => {
  const { badge, title, subtitle, steps, footerNote, footerNoteHighlight } = data;
  const visibleSteps = steps.slice(0, 3);

  return (
    <section
      className={`${classes.section}${className ? ` ${className}` : ''}`}
      aria-labelledby="timeline-title"
    >
      <div className={classes.container}>
        <div className={classes.header}>
          {badge && <span className={classes.badge}>{badge}</span>}
          <h2 id="timeline-title" className={classes.title}>
            {title}
          </h2>
          {subtitle && <p className={classes.subtitle}>{subtitle}</p>}
        </div>

        <div className={classes.timeline} role="list">
          {visibleSteps.map((step, index) => {
            const StepIcon = stepIcons[index % stepIcons.length];
            const isLast = index === visibleSteps.length - 1;
            return (
              <article key={`${step.title}-${index}`} className={classes.step} role="listitem">
                <div className={classes.stepMarker}>
                  <span className={classes.stepNumber}>{`0${index + 1}`}</span>
                  <span className={classes.stepIcon} aria-hidden="true">
                    <StepIcon size={20} stroke={2} />
                  </span>
                  {!isLast && <span className={classes.connector} aria-hidden="true" />}
                </div>
                <h3 className={classes.stepTitle}>{step.title}</h3>
                <p className={classes.stepDescription}>{step.description}</p>
              </article>
            );
          })}
        </div>

        {footerNote && (
          <p className={classes.resultNote}>
            <span>{footerNote}</span>
            {footerNoteHighlight && (
              <span className={classes.resultHighlight}> {footerNoteHighlight}</span>
            )}
          </p>
        )}
      </div>
    </section>
  );
};
